/**
 * CloudReauthModal - Shown when cloud auth expires during an LLM call
 *
 * Listens for the 'cloud-auth-invalidated' DOM event (dispatched by
 * PlatformContext when the main process detects an unrecoverable 401).
 * Offers re-sign-in via OAuth or dismissal to continue with local models.
 *
 * @module @humanizer/studio/components/auth/CloudReauthModal
 */

import { useState, useEffect, useCallback } from 'react';
import { AUTH_API_URL } from '../../config';
import './cloud-reauth-modal.css';

export function CloudReauthModal() {
  const [visible, setVisible] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  // Only relevant on Electron
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  // Listen for the DOM event from PlatformContext
  useEffect(() => {
    if (!isElectron) return;

    const handler = () => setVisible(true);
    window.addEventListener('cloud-auth-invalidated', handler);
    return () => window.removeEventListener('cloud-auth-invalidated', handler);
  }, [isElectron]);

  // Listen for OAuth callback to auto-dismiss on successful re-auth
  useEffect(() => {
    if (!visible || !window.electronCloudAuth) return;

    const unsubscribe = window.electronCloudAuth.onOAuthCallback((data) => {
      setSigningIn(false);
      if (data.token) {
        setVisible(false);
      }
    });

    return unsubscribe;
  }, [visible]);

  const handleSignIn = useCallback(async (provider: 'google' | 'github' | 'discord') => {
    if (!window.electronCloudAuth || !window.electronAPI) return;

    setSigningIn(true);
    try {
      const { callbackUrl } = await window.electronCloudAuth.signIn();
      const oauthUrl = `${AUTH_API_URL}/auth/oauth/${provider}/login?tenant=humanizer&redirect=${encodeURIComponent(callbackUrl)}`;
      await window.electronAPI.shell.openExternal(oauthUrl);
    } catch {
      setSigningIn(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setSigningIn(false);
  }, []);

  if (!visible || !isElectron) return null;

  return (
    <div className="cloud-reauth__overlay" role="dialog" aria-modal="true" aria-label="Cloud session expired">
      <div className="cloud-reauth">
        <h2 className="cloud-reauth__title">Cloud Session Expired</h2>
        <p className="cloud-reauth__message">
          Your cloud authentication has expired. Sign in again to continue
          using cloud-powered models, or switch to local models.
        </p>

        <div className="cloud-reauth__actions">
          <button
            className="btn btn--primary"
            onClick={() => handleSignIn('google')}
            disabled={signingIn}
          >
            {signingIn ? 'Waiting for browser...' : 'Sign In Again'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={handleDismiss}
            disabled={signingIn}
          >
            Use Local Models
          </button>
        </div>

        {signingIn && (
          <p className="cloud-reauth__hint">
            Complete sign-in in your browser. This dialog will close automatically.
          </p>
        )}
      </div>
    </div>
  );
}
